import { captureException } from '@sentry/nextjs';
import { BasketTransferError } from '@repo/ui/src/utils/errors';
import {
  GraphQLRequestWithTokenProps,
  rawRequestWithToken,
  DefaultVariables,
} from '@utils/graphql-request';
import { publicRuntimeConfig } from '@utils/publicRuntimeConfig';
import { getShopTokens, setShopTokens } from '@utils/userTokens';

export interface BasketRequestProps<
  Variables extends DefaultVariables = DefaultVariables,
> extends GraphQLRequestWithTokenProps<Variables> {
  country: string;
}

export const basketRequest = async <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>(
  props: BasketRequestProps<Variables>,
): Promise<Data | undefined> => {
  try {
    const { shopToken, basketJWT } = getShopTokens();
    const requestHeaders = new Headers(Object.entries(props.requestHeaders ?? {}));
    if (shopToken) {
      requestHeaders.set('x-shop-token', shopToken);
    }
    if (basketJWT) {
      requestHeaders.set('x-basket-token', basketJWT);
    }
    const { data, headers } =
      (await rawRequestWithToken<Data, Variables>({
        query: props.document,
        ...props,
        requestHeaders,
      } as any)) ?? {};

    const newShopToken = headers?.get('x-shop-token');
    const newBasketJWT = headers?.get('x-basket-token');
    if (newShopToken != null || newBasketJWT != null) {
      setShopTokens({
        ...(newShopToken != null && { shopToken: newShopToken }),
        ...(newBasketJWT != null && { basketJWT: newBasketJWT }),
      });
    }

    return data;
  } catch (e) {
    captureException(e);
    if (
      publicRuntimeConfig.commerceProvider === 'shopify' &&
      (e as any).response.errors.some((err: any) => err.code === 401)
    ) {
      return;
    }
    throw new BasketTransferError();
  }
};

export class OutOfStockError extends Error {
  info: any;
  constructor(info: any) {
    super('Out of Stock');
    this.info = info;
  }
}
